/**
 * UpdateGroupModal Component
 *
 * Modal for updating group name and description.
 */

import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Modal } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Textarea } from '../../../components/ui/Textarea';
import { Alert } from '../../../components/ui/Alert';
import { useUpdateGroup } from '../hooks/useUpdateGroup';
import type { GroupSanitized } from '../../../types/group.types';
import { getErrorMessage } from '../../../lib/utils/errors';

// Validation schema
const updateGroupSchema = z.object({
  groupName: z.string().min(1, 'Group name is required').max(100, 'Group name must not exceed 100 characters'),
  description: z.string().max(500, 'Description must not exceed 500 characters').optional(),
});

type UpdateGroupFormData = z.infer<typeof updateGroupSchema>;

interface UpdateGroupModalProps {
  group: GroupSanitized | null;
  tenantId: string;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const UpdateGroupModal: React.FC<UpdateGroupModalProps> = ({
  group,
  tenantId,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const updateGroupMutation = useUpdateGroup(tenantId);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<UpdateGroupFormData>({
    resolver: zodResolver(updateGroupSchema),
  });

  // Populate form when group changes
  useEffect(() => {
    if (group) {
      reset({
        groupName: group.groupName,
        description: group.description || '',
      });
    }
  }, [group, reset]);

  const onSubmit = async (data: UpdateGroupFormData) => {
    if (!group) return;

    try {
      await updateGroupMutation.mutateAsync({
        groupId: group.groupId,
        data,
      });
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Failed to update group:', error);
    }
  };

  const handleClose = () => {
    reset();
    updateGroupMutation.reset();
    onClose();
  };

  if (!group) return null;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md">
      <Modal.Header>Edit Group</Modal.Header>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Modal.Body>
          <div className="space-y-4">
            {updateGroupMutation.isError && (
              <Alert variant="error">
                {getErrorMessage(updateGroupMutation.error)}
              </Alert>
            )}

            {/* Group Code (Read-Only) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Group Code
              </label>
              <div className="flex items-center gap-2">
                <code className="flex-1 px-3 py-2 bg-gray-100 border border-gray-300 rounded-md text-sm text-gray-700 font-mono">
                  {group.groupCode}
                </code>
                <span className="text-xs text-gray-500 bg-gray-200 px-2 py-1 rounded">
                  Immutable
                </span>
              </div>
            </div>

            <div>
              <label htmlFor="groupName" className="block text-sm font-medium text-gray-700">
                Group Name <span className="text-red-500">*</span>
              </label>
              <Input
                id="groupName"
                type="text"
                {...register('groupName')}
                error={errors.groupName?.message}
                className="mt-1"
              />
            </div>

            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                Description
              </label>
              <Textarea
                id="description"
                {...register('description')}
                error={errors.description?.message}
                rows={3}
                className="mt-1"
              />
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button type="button" variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={isSubmitting || updateGroupMutation.isPending}
          >
            Save Changes
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};
